<template>
  <div class="card-pile-counter" :class="`team-${team.toLowerCase()}`">
    <p class="counting-label">Now counting total card points...</p>

    <!-- Single row of 100-point piles (matching CanastaDisplay layout) -->
    <div class="pile-row">
      <div
        v-for="(pile, index) in displayedPiles"
        :key="`pile-${index}`"
        class="pile-stack"
        ref="pileRefs"
        :class="{
          counting: highlightedIndex === index,
          counted: countedIndices.includes(index),
        }"
        :style="getPileStyle(index)"
      >
        <!-- Stacked cards for depth effect -->
        <div
          v-for="i in 3"
          :key="`back-${i}`"
          class="stack-card"
          :style="getStackCardStyle(i)"
        >
          <Card :card="null" :faceDown="true" size="small" />
        </div>
        <div class="stack-card top-card" :style="getStackCardStyle(4)">
          <Card :card="pile.topCard as any" size="small" />
        </div>
      </div>

      <!-- Remainder cards (points < 100) -->
      <div v-if="remainder > 0 && showRemainder" class="remainder-section">
        <span class="remainder-plus">+</span>
        <div class="remainder-cards">
          <div
            v-for="(card, index) in remainderCards"
            :key="`remainder-${index}`"
            class="remainder-card"
            :style="getRemainderCardStyle(index)"
          >
            <Card :card="card" size="small" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="totalPiles === 0 && remainder === 0" class="no-piles">
        No card points
      </div>
    </div>

    <!-- Total -->
    <div class="pile-total">
      <span class="total-value">{{ displayValue.toLocaleString() }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoundEndAnimations } from '../../composables/useRoundEndAnimations'
import { useSounds } from '../../composables/useSounds'
import Card from '../cards/Card.vue'

interface CardData {
  rank: string
  suit: string
}

interface PileData {
  topCard: CardData
}

interface Props {
  team: 'A' | 'B'
  teamName: string
  cardPoints: number
  canastaBase?: number
  isAnimating?: boolean
  showCombined?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canastaBase: 0,
  isAnimating: false,
  showCombined: false,
})

const emit = defineEmits<{
  dealComplete: [total: number]  // Emitted after piles are dealt, before counting
  countComplete: [total: number]  // Emitted after counting animation completes
  pileHighlighted: [index: number]
}>()

const { generateNaturalPosition } = useRoundEndAnimations()
const { playPileAppear, playPileHighlight } = useSounds()

// Refs
const pileRefs = ref<HTMLElement[]>([])

// Animation state
const displayedPileCount = ref(0)
const highlightedIndex = ref(-1)
const countedIndices = ref<number[]>([])
const displayValue = ref(0)
const isCountAnimating = ref(false)
const showRemainder = ref(false)

// Calculate pile count and remainder
const totalPiles = computed(() => Math.floor(props.cardPoints / 100))
const remainder = computed(() => props.cardPoints % 100)

// Generate representative cards for remainder points
// Card values: Joker=50, 2/A=20, K-8=10, 7-4=5
const remainderCards = computed<CardData[]>(() => {
  const cards: CardData[] = []
  let remaining = remainder.value
  const suits = ['hearts', 'diamonds', 'clubs', 'spades']
  let suitIndex = 0

  // Add Jokers (50 pts each)
  while (remaining >= 50) {
    cards.push({ rank: 'JOKER', suit: 'JOKER' })
    remaining -= 50
  }

  // Add 2s or Aces (20 pts each)
  while (remaining >= 20) {
    cards.push({ rank: remaining % 40 === 0 ? '2' : 'A', suit: suits[suitIndex++ % 4] })
    remaining -= 20
  }

  // Add 10-point cards (K, Q, J, 10, 9, 8)
  const tenPointRanks = ['K', 'Q', 'J', '10', '9', '8']
  let tenIdx = 0
  while (remaining >= 10) {
    cards.push({ rank: tenPointRanks[tenIdx++ % 6], suit: suits[suitIndex++ % 4] })
    remaining -= 10
  }

  // Add 5-point cards (7, 6, 5, 4)
  const fivePointRanks = ['7', '6', '5', '4']
  let fiveIdx = 0
  while (remaining >= 5) {
    cards.push({ rank: fivePointRanks[fiveIdx++ % 4], suit: suits[suitIndex++ % 4] })
    remaining -= 5
  }

  return cards
})

// Build pile data - simple top card for each pile (matching CanastaDisplay style)
const allPiles = computed<PileData[]>(() => {
  const ranks = ['K', 'Q', 'J', '10', '9', '8', '7', '6'] as const
  const suits = ['hearts', 'diamonds', 'clubs', 'spades'] as const

  return Array.from({ length: totalPiles.value }, (_, i) => ({
    topCard: {
      rank: ranks[i % ranks.length] as string,
      suit: suits[i % suits.length] as string,
    },
  }))
})

// Currently displayed piles (for animation)
const displayedPiles = computed(() => allPiles.value.slice(0, displayedPileCount.value))

// Random positions for natural dealing (cached)
const pilePositions = ref<
  Map<number, { rotation: number; offsetX: number; offsetY: number }>
>(new Map())

// Generate pile positions (matching CanastaDisplay)
function initPilePositions(): void {
  pilePositions.value.clear()

  for (let i = 0; i < totalPiles.value; i++) {
    const pos = generateNaturalPosition(0, 0, i, false)
    pilePositions.value.set(i, {
      rotation: pos.rotation,
      offsetX: pos.x,
      offsetY: pos.y,
    })
  }
}

// Get style for pile with natural positioning (matching CanastaDisplay)
function getPileStyle(index: number): Record<string, string> {
  const pos = pilePositions.value.get(index)

  if (!pos) {
    return { animationDelay: `${index * 0.12}s` }
  }

  return {
    transform: `rotate(${pos.rotation}deg) translate(${pos.offsetX}px, ${pos.offsetY}px)`,
    animationDelay: `${index * 0.12}s`,
  }
}

// Get card style within stack - 3D offset effect (matching CanastaDisplay)
function getStackCardStyle(cardIdx: number): Record<string, string> {
  const offset = (cardIdx - 1) * 2
  return {
    position: 'absolute',
    top: `${-offset}px`,
    left: '0',
    zIndex: String(cardIdx),
  }
}

// Get style for remainder cards - matching PlayerHandPenalty fan style
function getRemainderCardStyle(index: number): Record<string, string> {
  const total = remainderCards.value.length

  if (total === 0) return {}

  // Match PlayerHandPenalty constants
  const CARD_OVERLAP = 18 // pixels between cards
  const MAX_FAN_ANGLE = 30 // degrees total spread

  // Calculate rotation and position for fan effect (same as PlayerHandPenalty)
  const halfAngle = MAX_FAN_ANGLE / 2
  const angleStep = total > 1 ? MAX_FAN_ANGLE / (total - 1) : 0
  const rotation = total > 1 ? -halfAngle + angleStep * index : 0

  // Horizontal offset to center the fan
  const halfWidth = ((total - 1) * CARD_OVERLAP) / 2
  const xOffset = index * CARD_OVERLAP - halfWidth

  // Slight vertical arc
  const normalizedPos = total > 1 ? (index - (total - 1) / 2) / ((total - 1) / 2) : 0
  const yOffset = Math.abs(normalizedPos) * 5 // 5px arc at edges

  return {
    '--card-rotation': `${rotation}deg`,
    '--card-x-offset': `${xOffset}px`,
    '--card-y-offset': `${yOffset}px`,
    '--card-z-index': `${index}`,
  }
}

// Animate pile dealing (matching CanastaDisplay timing)
async function animatePileDealing(): Promise<void> {
  // Show all piles at once with stagger animation (CSS handles the stagger)
  displayedPileCount.value = totalPiles.value
  displayValue.value = props.cardPoints

  // Play sound for pile appearance
  if (totalPiles.value > 0 || remainder.value > 0) {
    playPileAppear()
  }

  // Show remainder after piles
  if (remainder.value > 0) {
    showRemainder.value = true
  }

  // Allow stagger animation to complete (120ms per stack)
  const animationDuration = Math.max(400, totalPiles.value * 120 + 400)
  await delay(animationDuration)
}

// Animate pile counting (Phase B - with highlighting)
async function animatePileCounting(voiceDuration: number): Promise<void> {
  if (totalPiles.value === 0) {
    // No piles to count, just emit complete
    emit('countComplete', props.cardPoints)
    return
  }

  // Initial delay to sync with voice startup latency (~700ms)
  // Web Speech API has startup delay before audio actually plays
  const voiceStartupDelay = 700
  await delay(voiceStartupDelay)

  // Use remaining time for counting intervals (full duration, no reduction)
  isCountAnimating.value = true
  const remainingDuration = Math.max(voiceDuration - voiceStartupDelay, totalPiles.value * 100)
  const interval = remainingDuration / totalPiles.value

  for (let i = 0; i < totalPiles.value; i++) {
    highlightedIndex.value = i
    playPileHighlight()
    emit('pileHighlighted', i)

    await delay(interval)

    highlightedIndex.value = -1
    countedIndices.value.push(i)
  }

  isCountAnimating.value = false
  emit('countComplete', props.cardPoints)
}

// Get pile elements for external use
function getPileElements(): HTMLElement[] {
  return pileRefs.value
}

// Delay helper
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Initialize on mount
onMounted(() => {
  initPilePositions()

  if (!props.isAnimating) {
    // Show all piles immediately
    displayedPileCount.value = totalPiles.value
    displayValue.value = props.cardPoints
    countedIndices.value = Array.from({ length: totalPiles.value }, (_, i) => i)
    showRemainder.value = remainder.value > 0
  }
})

// Watch for animation trigger - immediate: true ensures it fires on mount
watch(
  () => props.isAnimating,
  async (animating) => {
    if (animating) {
      displayedPileCount.value = 0
      displayValue.value = 0
      countedIndices.value = []
      showRemainder.value = false

      await nextTick()
      await animatePileDealing()

      // Emit when dealing complete - parent will orchestrate counting with voice
      emit('dealComplete', props.cardPoints)
    }
  },
  { immediate: true }
)

// Expose methods for parent orchestration
defineExpose({
  animatePileDealing,
  animatePileCounting,
  getPileElements,
})
</script>

<style scoped>
/* Minimal container - matching CanastaDisplay */
.card-pile-counter {
  padding: 1rem;
}

.counting-label {
  font-size: 1rem;
  font-weight: 500;
  font-style: italic;
  margin: 0 0 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
}

/* Single row of piles - matching CanastaDisplay */
.pile-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  align-items: flex-end;
  min-height: 120px;
  padding: 0.5rem;
}

/* Individual pile stack - matching canasta stacks */
.pile-stack {
  position: relative;
  width: 65px;
  height: 95px;
  animation: stackAppear 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  /* Gold glow for card point piles */
  filter: drop-shadow(0 0 8px rgba(232, 197, 71, 0.4));
}

@keyframes stackAppear {
  from {
    opacity: 0;
    transform: scale(0.6) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.pile-stack.counting {
  animation: countingPulse 0.3s ease-out;
  z-index: 10;
}

@keyframes countingPulse {
  50% {
    transform: scale(1.1);
    filter: brightness(1.2) drop-shadow(0 0 15px rgba(232, 197, 71, 0.6));
  }
}

.pile-stack.counted {
  filter: brightness(1.1) drop-shadow(0 0 8px rgba(232, 197, 71, 0.5));
}

/* Card positioning within stack */
.stack-card {
  position: absolute;
  width: 58px;
  height: 82px;
}

.stack-card.top-card {
  z-index: 10;
}

/* Empty state */
.no-piles {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.875rem;
  font-style: italic;
}

/* Remainder section - loose cards that don't make 100 */
.remainder-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: stackAppear 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  animation-delay: 0.3s;
}

.remainder-plus {
  font-size: 1.5rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
  margin-right: 0.25rem;
}

.remainder-cards {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 90px;
  min-width: 100px;
}

.remainder-card {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform-origin: bottom center;
  transform: translateX(calc(-50% + var(--card-x-offset, 0px)))
    translateY(var(--card-y-offset, 0px)) rotate(var(--card-rotation, 0deg));
  z-index: var(--card-z-index, 0);
  width: 58px;
  height: 82px;
  filter: drop-shadow(0 0 6px rgba(232, 197, 71, 0.3));
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Total - simple and clean, matching CanastaDisplay */
.pile-total {
  text-align: center;
  margin-top: 1.5rem;
}

.total-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #e8c547;
}
</style>
